"use client";

import { VEHICLES, VehicleId } from "@/config/infos";
import Image from "next/image";
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { RESERVATION_UI } from "./reservationFormUi";

type VehicleSelectorProps = {
  selectedVehicleId: VehicleId;
  onSelect: (vehicleId: VehicleId) => void;
  passengers: number;
  luggage: number;
  babySeats: number;
  childSeats: number;
  locale?: Locale;
};

export const VehicleSelector = ({
  selectedVehicleId,
  onSelect,
  passengers,
  luggage,
  babySeats,
  childSeats,
  locale = "fr",
}: VehicleSelectorProps) => {
  const totalPassengers = passengers + babySeats + childSeats;
  const t = tr(RESERVATION_UI, locale).vehicle;

  return (
    <div className="mb-6">
      <h3 className="mb-4 text-lg font-semibold text-black">{t.select}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(Object.keys(VEHICLES) as VehicleId[]).map((vehicleId) => {
          const vehicle = VEHICLES[vehicleId];
          const isSelected = selectedVehicleId === vehicleId;
          const exceedsPassengers = totalPassengers > vehicle.maxPassengers;
          const exceedsLuggage = luggage > vehicle.maxLuggage;
          const isDisabled = exceedsPassengers || exceedsLuggage;

          return (
            <button
              key={vehicleId}
              type="button"
              onClick={() => !isDisabled && onSelect(vehicleId)}
              disabled={isDisabled}
              className={`relative overflow-hidden rounded-lg border-2 p-4 transition-all ${
                isSelected
                  ? "border-gold-600 bg-gold-100"
                  : "border-black/15 bg-black/5"
              } ${
                isDisabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:border-gold-500 hover:bg-gold-100"
              }`}
            >
              {isSelected && (
                <div className="absolute right-2 top-2 z-10">
                  <Icon
                    icon="ph:check-circle-fill"
                    className="h-6 w-6 text-gold-700"
                  />
                </div>
              )}
              <div className="relative mb-3 aspect-video w-full overflow-hidden rounded-md bg-white/90">
                <Image
                  src={vehicle.image}
                  alt={vehicle.name}
                  width={300}
                  height={200}
                  className="h-full w-full object-contain p-2"
                />
              </div>
              <div className="text-start">
                <h4 className="mb-1 font-semibold text-black">
                  {vehicle.classLabel}
                </h4>
                <div className="space-y-1 text-sm text-black/70">
                  <div className="flex items-center gap-2">
                    <Icon icon="ph:users" className="h-4 w-4" />
                    <span>{t.upToPassengers(vehicle.maxPassengers)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon icon="ph:suitcase" className="h-4 w-4" />
                    <span>{t.upToLuggage(vehicle.maxLuggage)}</span>
                  </div>
                </div>
                {(exceedsPassengers || exceedsLuggage) && (
                  <div className="mt-2 text-xs text-red-400">
                    {exceedsPassengers && (
                      <div>
                        {t.tooManyPassengers(
                          totalPassengers,
                          vehicle.maxPassengers,
                        )}
                      </div>
                    )}
                    {exceedsLuggage && (
                      <div>{t.tooManyLuggage(luggage, vehicle.maxLuggage)}</div>
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
